import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import {projectTitleInitialState} from '../reducers/project-title';
import downloadBlob from '../lib/download-blob';

import {
    getStorage,
    ref,
    uploadBytes,
    getDownloadURL
} from 'firebase/storage';

import {
    getFirestore,
    collection,
    addDoc,
    getDoc,
    doc,
    serverTimestamp,
    getDocs,
    query,
    where
} from 'firebase/firestore';

import {auth} from '../auth/firebase';

/**
 * Project saver / loader component.
 *
 * Supports:
 * - Save to computer
 * - Save Therapy Project
 * - Save for Marketplace
 * - Open Therapy Project
 * - Open Marketplace Project
 */
class SB3Downloader extends React.Component {
    constructor (props) {
        super(props);

        bindAll(this, [
            'downloadProject',
            'saveToAlyd',
            'saveTherapyProject',
            'openTherapyProject',
            'openMarketplaceProject',
            'loadProjectFromUrl'
        ]);
    }

    showToast = (message = '✅ Project saved!', duration = 3000) => {
        if (typeof document === 'undefined') return;

        const toastId = 'alyd-sb3-save-toast';

        const existing = document.getElementById(toastId);

        if (existing) {
            existing.remove();
        }

        const toast = document.createElement('div');

        toast.id = toastId;
        toast.textContent = message;

        Object.assign(toast.style, {
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#4caf50',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 999999,
            fontSize: '14px',
            fontWeight: '600',
            maxWidth: '420px',
            whiteSpace: 'normal',
            textAlign: 'center'
        });

        document.body.appendChild(toast);

        setTimeout(() => {
            const element =
                document.getElementById(toastId);

            if (element) {
                element.remove();
            }
        }, duration);
    };

    checkSaveEntitlement = async (db, uid) => {
        const companyRef =
            doc(
                db,
                'companies',
                uid
            );

        const companySnap =
            await getDoc(companyRef);

        if (!companySnap.exists()) {
            return {
                ok: false,
                reason: 'company_required'
            };
        }

        const data =
            companySnap.data() || {};

        const hasScratchAddon =
            Boolean(
                data.addons?.scratch_editor
            );

        if (!hasScratchAddon) {
            return {
                ok: false,
                reason: 'scratch_addon_required'
            };
        }

        return {
            ok: true
        };
    };

    sanitizeFilename = name => {
        const trimmed =
            String(name || '').trim();

        const safeBase = trimmed
            .replace(/[\\/#?%*:|"<>]/g, '-')
            .replace(/\s+/g, ' ')
            .substring(0, 100);

        const base = safeBase.length ?
            safeBase :
            'Scratch Project';

        return base.toLowerCase().endsWith('.sb3') ?
            base :
            `${base}.sb3`;
    };

    showSavingOverlay = (message = 'Saving your project...') => {
    if (typeof document === 'undefined') return;

    const overlayId = 'alyd-sb3-saving-overlay';

    const existing =
        document.getElementById(overlayId);

    if (existing) {
        existing.remove();
    }

    const overlay =
        document.createElement('div');

    overlay.id = overlayId;

    Object.assign(overlay.style, {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        zIndex: 999998,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    });

    const modal =
        document.createElement('div');

    Object.assign(modal.style, {
        background: '#fff',
        borderRadius: '12px',
        padding: '28px 32px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        textAlign: 'center',
        minWidth: '280px',
        maxWidth: '420px'
    });

    const spinner =
        document.createElement('div');

    Object.assign(spinner.style, {
        width: '38px',
        height: '38px',
        border: '4px solid #e5e7eb',
        borderTopColor: '#4f46e5',
        borderRadius: '50%',
        margin: '0 auto 16px auto',
        animation: 'alydSavingSpin 0.8s linear infinite'
    });

    const text =
        document.createElement('div');

    text.textContent = message;

    Object.assign(text.style, {
        fontSize: '16px',
        fontWeight: '600',
        color: '#0f172a'
    });

    const subtext =
        document.createElement('div');

    subtext.textContent =
        'Please wait and do not close this page.';

    Object.assign(subtext.style, {
        marginTop: '8px',
        fontSize: '13px',
        color: '#64748b'
    });

    const style =
        document.createElement('style');

    style.id = 'alyd-saving-spinner-style';

    style.textContent = `
        @keyframes alydSavingSpin {
            from {
                transform: rotate(0deg);
            }

            to {
                transform: rotate(360deg);
            }
        }
    `;

    if (
        !document.getElementById(
            'alyd-saving-spinner-style'
        )
    ) {
        document.head.appendChild(style);
    }

    modal.appendChild(spinner);
    modal.appendChild(text);
    modal.appendChild(subtext);

    overlay.appendChild(modal);

    document.body.appendChild(overlay);
};

hideSavingOverlay = () => {
    if (typeof document === 'undefined') return;

    const overlay =
        document.getElementById(
            'alyd-sb3-saving-overlay'
        );

    if (overlay) {
        overlay.remove();
    }
};

    promptForFilename = () => {
        const defaultTitle =
            (this.props.projectFilename || '')
                .replace(/\.sb3$/i, '') ||
            'Scratch Project';

        const userTitle =
            window.prompt(
                'Name your project:',
                defaultTitle
            );

        if (userTitle === null) {
            return null;
        }

        return this.sanitizeFilename(userTitle);
    };

    getStageThumbnailBlob = async () => {
    const vm = this.props.vm;

    if (!vm?.renderer?.requestSnapshot) {
        console.warn('[THERAPY THUMBNAIL] Renderer snapshot unavailable');
        return null;
    }

    try {
        const dataURL = await new Promise(resolve => {
            vm.renderer.requestSnapshot(snapshot => {
                resolve(snapshot || null);
            });
        });

        if (!dataURL) {
            console.warn('[THERAPY THUMBNAIL] No snapshot returned');
            return null;
        }

        const response = await fetch(dataURL);

        if (!response.ok) {
            throw new Error(
                `Thumbnail conversion failed with status ${response.status}`
            );
        }

        return await response.blob();
    } catch (error) {
        console.warn(
            '[THERAPY THUMBNAIL] Snapshot failed:',
            error
        );

        return null;
    }
};

    downloadProject () {
        this.props.saveProjectSb3()
            .then(content => {
                if (this.props.onSaveFinished) {
                    this.props.onSaveFinished();
                }

                downloadBlob(
                    this.props.projectFilename,
                    content
                );
            });
    }

   saveTherapyProject () {
    this.props.saveProjectSb3()
        .then(async content => {
            const maxMb = 25;

            const sizeMb = (
                content.size /
                (1024 * 1024)
            ).toFixed(2);

            console.log(
                '[THERAPY SB3 SIZE]',
                sizeMb,
                'MB'
            );

            if (
                content.size >
                maxMb * 1024 * 1024
            ) {
                this.showToast(
                    `❌ Project is ${sizeMb} MB. Max allowed is ${maxMb} MB.`,
                    6000
                );

                return;
            }

            const user =
                auth.currentUser;

            if (!user) {
                window.alert(
                    'Please log in to save your therapy project.'
                );

                return;
            }

            const storage =
                getStorage();

            const db =
                getFirestore();

            try {
                const fileName =
                    this.promptForFilename();

                if (!fileName) {
                    return;
                }

                this.showSavingOverlay(
                    'Saving Therapy project...'
                );

                /*
                 * Therapy projects are stored separately
                 * from Marketplace working projects.
                 */
                const therapyProjectCollectionRef =
                    collection(
                        db,
                        `therapyProjects/${user.uid}/projects`
                    );

                /*
                 * Only ACTIVE Therapy projects count
                 * toward the 10-project limit.
                 */
                const activeTherapyQuery =
                    query(
                        therapyProjectCollectionRef,
                        where(
                            'isDeleted',
                            '==',
                            false
                        )
                    );

                const snapshot =
                    await getDocs(
                        activeTherapyQuery
                    );

                console.log(
                    '[THERAPY PROJECT COUNT]',
                    snapshot.size
                );

                if (
                    snapshot.size >= 10
                ) {
                    this.hideSavingOverlay();

                    this.showToast(
                        '❌ You can save up to 10 Therapy projects. Delete one to add more.',
                        6000
                    );

                    return;
                }

                /*
                 * Save SB3 file.
                 */
                const storageRef =
                    ref(
                        storage,
                        `therapyProjects/${user.uid}/${fileName}`
                    );

                await uploadBytes(
                    storageRef,
                    content
                );

                const downloadURL =
                    await getDownloadURL(
                        storageRef
                    );

                /*
                 * Create and save stage thumbnail.
                 */
                let thumbnailURL = null;

                const thumbnailBlob =
                    await this.getStageThumbnailBlob();

                if (thumbnailBlob) {
                    const thumbnailFileName =
                        fileName.replace(
                            /\.sb3$/i,
                            '.png'
                        );

                    const thumbnailRef =
                        ref(
                            storage,
                            `therapyProjects/${user.uid}/thumbnails/${thumbnailFileName}`
                        );

                    await uploadBytes(
                        thumbnailRef,
                        thumbnailBlob,
                        {
                            contentType: 'image/png'
                        }
                    );

                    thumbnailURL =
                        await getDownloadURL(
                            thumbnailRef
                        );

                    console.log(
                        '[THERAPY THUMBNAIL] Saved:',
                        thumbnailURL
                    );
                }

                /*
                 * Save Therapy project metadata.
                 */
                await addDoc(
                    therapyProjectCollectionRef,
                    {
                        filename:
                            fileName,

                        displayName:
                            fileName.replace(
                                /\.sb3$/i,
                                ''
                            ),

                        downloadURL,

                        storagePath:
                            storageRef.fullPath,

                        thumbnailURL,

                        therapy:
                            this.props.therapyProjectState || null,

                        // Active Therapy working project
                        isDeleted: false,

                        createdAt:
                            serverTimestamp(),

                        updatedAt:
                            serverTimestamp()
                    }
                );

                console.log(
                    '[THERAPY FIRESTORE] Metadata saved'
                );

                this.hideSavingOverlay();

                this.showToast(
                    '✅ Therapy project saved!'
                );

                if (
                    this.props.onSaveFinished
                ) {
                    this.props.onSaveFinished();
                }
            } catch (error) {
                this.hideSavingOverlay();

                console.error(
                    '[THERAPY FIREBASE] Save failed:',
                    error
                );

                window.alert(
                    `❌ Failed to save therapy project: ${error?.message || 'Unknown error'}`
                );
            }
        });
}

saveToAlyd () {
    this.props.saveProjectSb3()
        .then(async content => {
            const maxMb = 25;

            const sizeMb = (
                content.size /
                (1024 * 1024)
            ).toFixed(2);

            console.log(
                '[SB3 SIZE]',
                sizeMb,
                'MB'
            );

            if (
                content.size >
                maxMb * 1024 * 1024
            ) {
                this.showToast(
                    `❌ Project is ${sizeMb} MB. Max allowed is ${maxMb} MB. Shorten audio or remove unused assets.`,
                    6000
                );

                return;
            }

            const user =
                auth.currentUser;

            if (!user) {
                window.alert(
                    'Please log in to save your project.'
                );

                return;
            }

            const storage =
                getStorage();

            const db =
                getFirestore();

            try {
                const entitlement =
                    await this.checkSaveEntitlement(
                        db,
                        user.uid
                    );

                console.log(
                    '[SAVE ENTITLEMENT]',
                    entitlement
                );

                if (!entitlement.ok) {
                    if (
                        entitlement.reason ===
                        'company_required'
                    ) {
                        window.alert(
                            'Company account required to save Marketplace projects.'
                        );

                        return;
                    }

                    if (
                        entitlement.reason ===
                        'scratch_addon_required'
                    ) {
                        window.alert(
                            'Saving Marketplace projects requires the Scratch add-on.'
                        );

                        return;
                    }

                    window.alert(
                        `You are not allowed to save this project. Reason: ${entitlement.reason || 'unknown'}`
                    );

                    return;
                }

                const fileName =
                    this.promptForFilename();

                if (!fileName) {
                    return;
                }

                this.showSavingOverlay(
                    'Saving Marketplace project...'
                );

                const sbProductCollectionRef =
                    collection(
                        db,
                        `companies/${user.uid}/sbProducts`
                    );

                /*
                 * Count only ACTIVE Marketplace projects.
                 * Soft-deleted projects do not use one of
                 * the user's 10 Marketplace project slots.
                 */
                const activeMarketplaceQuery =
                    query(
                        sbProductCollectionRef,
                        where(
                            'isDeleted',
                            '==',
                            false
                        )
                    );

                const snapshot =
                    await getDocs(
                        activeMarketplaceQuery
                    );

                console.log(
                    '[SAVE PROJECT COUNT]',
                    snapshot.size
                );

                if (
                    snapshot.size >= 10
                ) {
                    this.hideSavingOverlay();

                    this.showToast(
                        '❌ You can save up to 10 Marketplace projects. Delete one to add more.',
                        6000
                    );

                    return;
                }

                const storageRef =
                    ref(
                        storage,
                        `sb3Projects/${user.uid}/${fileName}`
                    );

                await uploadBytes(
                    storageRef,
                    content
                );

                const downloadURL =
                    await getDownloadURL(
                        storageRef
                    );

                /*
                 * Preserve Code Building / hasCode logic.
                 *
                 * hasCode === true:
                 * project already contains code.
                 *
                 * hasCode === false:
                 * project does not contain code.
                 */
                const targets =
                    this.props.vm.runtime?.targets || [];

                const hasCode =
                    targets.some(target => {
                        const blocks =
                            target.blocks?._blocks || {};

                        return (
                            Object.keys(blocks).length > 0
                        );
                    });

                console.log(
                    '[MARKETPLACE SAVE] Project has code:',
                    hasCode
                );

                /*
                 * Save Marketplace project metadata.
                 *
                 * IMPORTANT:
                 * This is the Marketplace collection,
                 * NOT the Therapy project collection.
                 */
                await addDoc(
                    sbProductCollectionRef,
                    {
                        filename:
                            fileName,

                        displayName:
                            fileName.replace(
                                /\.sb3$/i,
                                ''
                            ),

                        downloadURL,

                        storagePath:
                            storageRef.fullPath,

                        therapy:
                            this.props.therapyProjectState || null,

                        hasCode,

                        /*
                         * New Marketplace projects start active.
                         * Delete logic can later soft-delete them
                         * by changing this to true.
                         */
                        isDeleted: false,

                        createdAt:
                            serverTimestamp()
                    }
                );

                console.log(
                    '[FIRESTORE] Marketplace metadata saved'
                );

                this.hideSavingOverlay();

                this.showToast(
                    '✅ Marketplace project saved!'
                );

                if (
                    this.props.onSaveFinished
                ) {
                    this.props.onSaveFinished();
                }
            } catch (error) {
                this.hideSavingOverlay();

                console.error(
                    '[FIREBASE] Marketplace save failed:',
                    error
                );

                window.alert(
                    `❌ Failed to save Marketplace project: ${error?.message || 'Unknown error'}`
                );
            }
        });
}

    async loadProjectFromUrl (
        downloadURL,
        displayName = 'Scratch Project'
    ) {
        try {
            console.log(
                '[ALYD OPEN] Downloading:',
                displayName
            );

            const response =
                await fetch(downloadURL);

            if (!response.ok) {
                throw new Error(
                    `Download failed with status ${response.status}`
                );
            }

            const arrayBuffer =
                await response.arrayBuffer();

            console.log(
                '[ALYD OPEN] Loading project into VM:',
                displayName
            );

            await this.props.loadProject(
                arrayBuffer
            );

            this.showToast(
                `✅ Opened ${displayName}`
            );
        } catch (error) {
            console.error(
                '[ALYD OPEN] Failed:',
                error
            );

            window.alert(
                `❌ Failed to open project: ${error?.message || 'Unknown error'}`
            );
        }
    }

    async openTherapyProject () {
        const user =
            auth.currentUser;

        if (!user) {
            window.alert(
                'Please log in to open a therapy project.'
            );

            return;
        }

        try {
            const db =
                getFirestore();

            const therapyRef =
                collection(
                    db,
                    `therapyProjects/${user.uid}/projects`
                );

            const snapshot =
                await getDocs(
                    therapyRef
                );

            if (snapshot.empty) {
                window.alert(
                    'You do not have any saved therapy projects yet.'
                );

                return;
            }

            const projects =
                snapshot.docs.map(projectDoc => ({
                    id:
                        projectDoc.id,
                    ...projectDoc.data()
                }));

            const projectList =
                projects
                    .map(
                        (project, index) =>
                            `${index + 1}. ${project.displayName || project.filename || 'Untitled'}`
                    )
                    .join('\n');

            const selection =
                window.prompt(
                    `Open Therapy Project:\n\n${projectList}\n\nEnter project number:`
                );

            if (selection === null) {
                return;
            }

            const selectedIndex =
                Number(selection) - 1;

            if (
                !Number.isInteger(selectedIndex) ||
                selectedIndex < 0 ||
                selectedIndex >= projects.length
            ) {
                window.alert(
                    'Please enter a valid project number.'
                );

                return;
            }

            const selectedProject =
                projects[selectedIndex];

            if (!selectedProject.downloadURL) {
                window.alert(
                    'This project does not have a valid download URL.'
                );

                return;
            }

           await this.loadProjectFromUrl(
    selectedProject.downloadURL,
    selectedProject.displayName ||
        selectedProject.filename ||
        'Therapy Project'
);

if (
    selectedProject.therapy &&
    this.props.onRestoreTherapyProject
) {
    this.props.onRestoreTherapyProject(
        selectedProject.therapy
    );
}
        } catch (error) {
            console.error(
                '[THERAPY OPEN] Failed:',
                error
            );

            window.alert(
                `❌ Failed to load therapy projects: ${error?.message || 'Unknown error'}`
            );
        }
    }

    async openMarketplaceProject () {
        const user =
            auth.currentUser;

        if (!user) {
            window.alert(
                'Please log in to open a Marketplace project.'
            );

            return;
        }

        const db =
            getFirestore();

        try {
            const entitlement =
                await this.checkSaveEntitlement(
                    db,
                    user.uid
                );

            if (!entitlement.ok) {
                if (
                    entitlement.reason ===
                    'company_required'
                ) {
                    window.alert(
                        'Company account required to open Marketplace projects.'
                    );

                    return;
                }

                if (
                    entitlement.reason ===
                    'scratch_addon_required'
                ) {
                    window.alert(
                        'Opening Marketplace projects requires the Scratch add-on.'
                    );

                    return;
                }

                return;
            }

            const marketplaceRef =
                collection(
                    db,
                    `companies/${user.uid}/sbProducts`
                );

            const snapshot =
                await getDocs(
                    marketplaceRef
                );

            if (snapshot.empty) {
                window.alert(
                    'You do not have any saved Marketplace projects yet.'
                );

                return;
            }

            const projects =
                snapshot.docs.map(projectDoc => ({
                    id:
                        projectDoc.id,
                    ...projectDoc.data()
                }));

            const projectList =
                projects
                    .map(
                        (project, index) =>
                            `${index + 1}. ${project.displayName || project.filename || 'Untitled'}`
                    )
                    .join('\n');

            const selection =
                window.prompt(
                    `Open Marketplace Project:\n\n${projectList}\n\nEnter project number:`
                );

            if (selection === null) {
                return;
            }

            const selectedIndex =
                Number(selection) - 1;

            if (
                !Number.isInteger(selectedIndex) ||
                selectedIndex < 0 ||
                selectedIndex >= projects.length
            ) {
                window.alert(
                    'Please enter a valid project number.'
                );

                return;
            }

            const selectedProject =
                projects[selectedIndex];

            if (!selectedProject.downloadURL) {
                window.alert(
                    'This project does not have a valid download URL.'
                );

                return;
            }

            await this.loadProjectFromUrl(
                selectedProject.downloadURL,
                selectedProject.displayName ||
                    selectedProject.filename ||
                    'Marketplace Project'
            );
        } catch (error) {
            console.error(
                '[MARKETPLACE OPEN] Failed:',
                error
            );

            window.alert(
                `❌ Failed to load Marketplace projects: ${error?.message || 'Unknown error'}`
            );
        }
    }

    render () {
        const {
            children
        } = this.props;

        return children(
            this.props.className,
            this.downloadProject,
            this.saveToAlyd,
            this.saveTherapyProject,
            this.openTherapyProject,
            this.openMarketplaceProject
        );
    }
}

const getProjectFilename = (
    curTitle,
    defaultTitle
) => {
    let filenameTitle =
        curTitle;

    if (
        !filenameTitle ||
        filenameTitle.length === 0
    ) {
        filenameTitle =
            defaultTitle;
    }

    return `${filenameTitle.substring(0, 100)}.sb3`;
};

SB3Downloader.propTypes = {
    children:
        PropTypes.func,
    vm:
    PropTypes.object,
    className:
        PropTypes.string,
    loadProject:
        PropTypes.func,
    onSaveFinished:
        PropTypes.func,
    projectFilename:
        PropTypes.string,
    saveProjectSb3:
        PropTypes.func,
        therapyProjectState: PropTypes.object,
        onRestoreTherapyProject: PropTypes.func,
};

SB3Downloader.defaultProps = {
    className: ''
};

const mapStateToProps = state => ({
    vm:
        state.scratchGui.vm,

    saveProjectSb3:
        state.scratchGui.vm.saveProjectSb3.bind(
            state.scratchGui.vm
        ),

    loadProject:
        state.scratchGui.vm.loadProject.bind(
            state.scratchGui.vm
        ),

    projectFilename:
        getProjectFilename(
            state.scratchGui.projectTitle,
            projectTitleInitialState
        )
});

export default connect(
    mapStateToProps,
    () => ({})
)(SB3Downloader);